/**
 * `/settings/integrations` — provider catalogue.
 *
 * Gap M6: no backend today. The Stage 4 `useIntegrations()` hook
 * returns a hard-coded list of providers with
 * `status: 'coming_soon'`. We render one row per provider so the
 * topology slot is real from day one; each row flips to "Connect"
 * once the backend ships OAuth for that provider.
 */
import { EmptyState } from '@/components/primitives/EmptyState';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toastInfo } from '@/lib/errors';
import type { IntegrationProvider } from '@/lib/queries';
import { useIntegrations } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { PuzzlePiece } from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/settings/integrations')({
  component: IntegrationsSettings,
});

function IntegrationsSettings(): ReactElement {
  const integrations = useIntegrations();
  const providers = integrations.data ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-base font-semibold text-[var(--color-ink)]">Integrations</h2>
        <p className="text-sm text-[var(--color-mute)]">
          First-party connectors for popular tools. Vote on priorities via support@clickweave.co.
        </p>
      </header>

      {integrations.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {!integrations.isLoading && providers.length === 0 && (
        <EmptyState
          icon={PuzzlePiece}
          title="No integrations available yet"
          description="Check back soon — first-party Slack, GitHub, and Discord connectors are coming."
        />
      )}

      {!integrations.isLoading && providers.length > 0 && (
        <ul className="space-y-2">
          {providers.map((p) => (
            <IntegrationRow key={p.id} provider={p} />
          ))}
        </ul>
      )}
    </div>
  );
}

function IntegrationRow({ provider }: { provider: IntegrationProvider }): ReactElement {
  const isConnected = provider.status === 'connected';
  const isAvailable = provider.status === 'available';

  return (
    <li className="flex flex-col items-start justify-between gap-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4 sm:flex-row sm:items-center">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex size-10 items-center justify-center rounded-lg',
            isConnected
              ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
              : 'bg-[var(--color-paper-alt)] text-[var(--color-ink-soft)]',
          )}
        >
          <PuzzlePiece aria-hidden="true" size={20} weight="duotone" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-ink)]">{provider.name}</h3>
          <p className="text-xs text-[var(--color-mute)]">{provider.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {isConnected && (
          <span className="rounded-full bg-[var(--color-success)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-success)]">
            Connected
          </span>
        )}
        {isAvailable && (
          <Button
            onClick={() =>
              toastInfo(
                `${provider.name} is on the way`,
                'OAuth handoff will ship alongside the backend connector.',
              )
            }
          >
            Connect
          </Button>
        )}
        {provider.status === 'coming_soon' && (
          <span className="rounded-full bg-[var(--color-paper-alt)] px-2 py-0.5 text-xs font-medium text-[var(--color-ink-soft)]">
            Coming soon
          </span>
        )}
      </div>
    </li>
  );
}
